// Cross-tab signals. A leaf module: it imports nothing, so context/ can use it
// without closing an import cycle (see the note at the top of RegisterContext).
//
// Why this exists: RegisterContext used to listen for `storage` events on its own
// `activeReg` cache and re-ask the server whenever another tab wrote it. But every
// answer WROTE that cache — so the cache write doubled as a command to every other
// tab. Two tabs that got different answers (one per branch) re-triggered each other
// forever: A deletes → B re-asks and writes → A re-asks and deletes → …
//
// The rule here: a signal is SENT only by a deliberate user action (open, switch or
// close a till; switch branch), and RECEIVING one only ever acts on the receiving tab.
// Nothing that reacts to a signal publishes one, so an echo is structurally impossible.

const CHANNEL = "erp-tab-sync";
const FALLBACK_KEY = "__tabSync";

// Identifies this tab so the storage fallback can drop our own writes.
const TAB_ID = Math.random().toString(36).slice(2) + Date.now().toString(36);

let channel = null;
try {
  if (typeof BroadcastChannel !== "undefined") channel = new BroadcastChannel(CHANNEL);
} catch {
  channel = null;
}

const listeners = new Map(); // topic -> Set<handler>

const deliver = (msg) => {
  if (!msg || msg.from === TAB_ID) return;
  const set = listeners.get(msg.topic);
  if (!set) return;
  set.forEach((fn) => {
    try {
      fn(msg.payload);
    } catch (err) {
      console.error("tabSync handler failed:", err);
    }
  });
};

if (channel) {
  channel.onmessage = (e) => deliver(e.data);
} else if (typeof window !== "undefined") {
  // Fallback: `storage` fires only in OTHER tabs, and only when the value changes —
  // the nonce makes every signal a distinct value.
  window.addEventListener("storage", (e) => {
    if (e.key !== FALLBACK_KEY || !e.newValue) return;
    try {
      deliver(JSON.parse(e.newValue));
    } catch {
      // Not ours / malformed — ignore.
    }
  });
}

export const publish = (topic, payload = null) => {
  const msg = { topic, payload, from: TAB_ID, nonce: Math.random() };
  if (channel) {
    channel.postMessage(msg);
    return;
  }
  try {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify(msg));
    localStorage.removeItem(FALLBACK_KEY);
  } catch {
    // Storage blocked (private mode) — other tabs catch up on focus instead.
  }
};

export const subscribe = (topic, handler) => {
  if (!listeners.has(topic)) listeners.set(topic, new Set());
  listeners.get(topic).add(handler);
  return () => listeners.get(topic)?.delete(handler);
};

export const TOPIC = {
  REGISTER_CHANGED: "register:changed",
  BIZ_CONTEXT_CHANGED: "bizContext:changed",
};
